/**
 * UsageTab — Firestore usage + estimated monthly cost.
 * Reads app_config/usage_snapshot (written every 6h by scheduledUsageSnapshot,
 * or on demand via adminUsageSnapshot). The billing console stays authoritative.
 */
import React, { useEffect, useState } from 'react';
import { View, Text, TouchableOpacity, Pressable, ScrollView, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, getAdminAuthHeaders } from '../firebaseConfig';
import { getBase } from './entityResolutionConstants';
import { showToast } from '../utils/toast';
import { adminStyles as styles } from './adminStyles';
import { fmtCount, fmtGiB, fmtUsd, orphanStatus, latestStorage, freezeLabel } from '../utils/usageFormat';

const BAR = '#60a5fa';
const INK = 'rgba(255,255,255,0.92)';
const MUTED = 'rgba(255,255,255,0.55)';
const CARD = { backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)', padding: 14 };

function StatTile({ label, value, sub }) {
  return (
    <View style={[CARD, { flexGrow: 1, flexBasis: 180, minWidth: 160 }]}>
      <Text style={{ color: MUTED, fontSize: 12, fontWeight: '600' }}>{label}</Text>
      <Text style={{ color: INK, fontSize: 24, fontWeight: '700', marginTop: 4 }}>{value}</Text>
      {sub ? <Text style={{ color: MUTED, fontSize: 12, marginTop: 2 }}>{sub}</Text> : null}
    </View>
  );
}

// One-series daily bar chart. Hover (web) or tap shows the day's value.
function DailyBars({ title, days, field, format }) {
  const [hover, setHover] = useState(null);
  const values = days.map((d) => d[field] || 0);
  const max = Math.max(1, ...values);
  const shown = hover != null ? days[hover] : days[days.length - 1];
  return (
    <View style={[CARD, { marginBottom: 12 }]}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
        <Text style={{ color: INK, fontWeight: '700' }}>{title}</Text>
        <Text style={{ color: MUTED, fontSize: 12 }}>
          {shown ? `${shown.date}: ${format(shown[field] || 0)}` : ''}
        </Text>
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', height: 90, gap: 2, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.15)' }}>
        {days.map((d, i) => (
          <Pressable
            key={d.date}
            accessibilityLabel={`${d.date}: ${format(values[i])}`}
            onPress={() => setHover(i)}
            onHoverIn={() => setHover(i)}
            onHoverOut={() => setHover(null)}
            style={{ flex: 1, height: '100%', justifyContent: 'flex-end' }}
          >
            <View style={{
              height: `${Math.max(values[i] > 0 ? 2 : 0, (values[i] / max) * 100)}%`,
              backgroundColor: BAR,
              opacity: hover == null || hover === i ? 1 : 0.45,
              borderTopLeftRadius: 3, borderTopRightRadius: 3,
            }} />
          </Pressable>
        ))}
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 4 }}>
        <Text style={{ color: MUTED, fontSize: 11 }}>{days[0]?.date}</Text>
        <Text style={{ color: MUTED, fontSize: 11 }}>max {format(max)}</Text>
        <Text style={{ color: MUTED, fontSize: 11 }}>{days[days.length - 1]?.date}</Text>
      </View>
    </View>
  );
}

export default function UsageTab() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'app_config', 'usage_snapshot'), (s) => {
      setSnap(s.exists() ? s.data() : null);
      setLoading(false);
    }, () => setLoading(false));
    return () => unsub();
  }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${getBase()}/adminUsageSnapshot`, { method: 'POST', headers: await getAdminAuthHeaders() });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `HTTP ${res.status}`);
      showToast('Usage refreshed', 'success');
    } catch (e) {
      showToast(`Refresh failed: ${e.message}`, 'error');
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <ActivityIndicator color="#fff" style={{ marginTop: 40 }} />;

  const days = snap?.days || [];
  const est = snap?.estimate || {};
  const storage = latestStorage(days);
  const orphan = orphanStatus(snap?.orphans);

  return (
    <ScrollView style={styles.tabWrap} contentContainerStyle={{ paddingBottom: 40 }}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <Text style={styles.sectionTitle}>Usage & Cost</Text>
        <TouchableOpacity style={styles.smallBtn} onPress={refresh} disabled={refreshing}>
          <Text style={styles.smallBtnText}>{refreshing ? 'Refreshing…' : 'Refresh now'}</Text>
        </TouchableOpacity>
      </View>
      <Text style={[styles.caption, { marginTop: 0, marginBottom: 12 }]}>
        {snap ? `Updated ${new Date(snap.generated_at).toLocaleString()} · ${freezeLabel(snap.freeze_mode)} · updates every 6 hours` : 'No snapshot yet. Press Refresh now.'}
      </Text>

      {snap ? (
        <>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
            <StatTile label="Est. Firestore / month" value={fmtUsd(est.total)} sub="7-day avg ops + current storage" />
            <StatTile label="Database size" value={fmtGiB(storage.dataBytes)} sub={`PITR ${fmtGiB(storage.pitrBytes)} · backups ${fmtGiB(storage.backupBytes)}`} />
            <StatTile label="Reads / day" value={fmtCount(est.perDay?.reads)} sub={`${fmtUsd(est.reads)}/mo`} />
            <StatTile label="Writes / day" value={fmtCount(est.perDay?.writes)} sub={`${fmtUsd(est.writes)}/mo`} />
          </View>

          <View style={[CARD, { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 }]}>
            <Ionicons name={orphan.ok ? 'checkmark-circle' : 'warning'} size={20} color={orphan.ok ? '#4ade80' : '#fbbf24'} />
            <View style={{ flex: 1 }}>
              <Text style={{ color: INK, fontWeight: '700' }}>{orphan.ok ? 'No orphaned odds' : 'Orphaned odds found'}</Text>
              <Text style={{ color: MUTED, fontSize: 12 }}>{orphan.detail}</Text>
              {snap.orphans_checked_at ? <Text style={{ color: MUTED, fontSize: 11 }}>Checked {new Date(snap.orphans_checked_at).toLocaleString()} (daily)</Text> : null}
            </View>
          </View>

          <DailyBars title="Writes per day" days={days} field="writes" format={fmtCount} />
          <DailyBars title="Reads per day" days={days} field="reads" format={fmtCount} />
          <DailyBars title="Database size (data + indexes)" days={days} field="dataBytes" format={fmtGiB} />
          <DailyBars title="Backup storage" days={days} field="backupBytes" format={fmtGiB} />

          <View style={[CARD, { marginBottom: 12 }]}>
            <Text style={{ color: INK, fontWeight: '700', marginBottom: 8 }}>Monthly estimate by line</Text>
            {['storage', 'pitr', 'backups', 'reads', 'writes', 'deletes'].map((k) => (
              <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 }}>
                <Text style={{ color: MUTED }}>{k}</Text>
                <Text style={{ color: INK }}>{fmtUsd(est[k])}</Text>
              </View>
            ))}
            <Text style={[styles.caption, { fontSize: 11 }]}>{est.basis}</Text>
          </View>

          <View style={[CARD, { marginBottom: 12 }]}>
            <Text style={{ color: INK, fontWeight: '700', marginBottom: 8 }}>Document counts</Text>
            {Object.entries(snap.counts || {}).map(([k, v]) => (
              <View key={k} style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3 }}>
                <Text style={{ color: MUTED }}>{k}</Text>
                <Text style={{ color: INK }}>{fmtCount(v)}</Text>
              </View>
            ))}
          </View>

          {snap.errors?.length ? (
            <View style={CARD}>
              <Text style={{ color: '#fbbf24', fontWeight: '700' }}>Partial data</Text>
              {snap.errors.map((e, i) => <Text key={i} style={{ color: MUTED, fontSize: 12 }}>{e}</Text>)}
            </View>
          ) : null}
        </>
      ) : null}
    </ScrollView>
  );
}
